import {
    useMemo, useCallback, useState, useEffect,
} from 'react';
import { HubConnectionState, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import Security from 'additiv-services/services/core/security';

export const SET_NOTIFICATIONS = 'SET_NOTIFICATIONS';
export const ADD_NOTIFICATION = 'ADD_NOTIFICATION';
export const READ_NOTIFICATION = 'READ_NOTIFICATION';

/**
 * Hook for managing connection
 * @param {Object} config
 * @param {String} config.url
 * @param {String} config.accessToken
 * @param {String} config.oidcUserAccessToken
 * @param {Number} config.debugLevel
 * @param {Boolean} config.startWhenReady
 * @param {number} config.automaticReconnectTimeout
 * @param {Function} config.onUnauthorized
 * @returns {{stop: (function(): void)|*, connectionState: unknown, start: (function(): Promise<void>)|*, setup: setup, error: unknown, notifications: *, connection: HubConnection }}
 */

export const useConnection = (config) => {
    const [error, setError] = useState(undefined);
    const [connectionState, setConnectionState] = useState(HubConnectionState.Disconnected);
    const [connection, setConnection] = useState();
    const [settings, setSettings] = useState(config);

    async function getAccessToken() {
        try {
            const token = await Security.generateNotificationHubAccessToken(settings.oidcUserAccessToken);

            return (token as string) || settings.accessToken || '';
        } catch (err) {
            console.log('Error of getting refreshed notification access token for Notification Hub', err);

            return settings.accessToken || '';
        }
    }

    const init = useCallback(() => {
        try {
            setError(undefined);

            const conn = new HubConnectionBuilder()
                .withUrl(settings.url, {
                    accessTokenFactory: () => getAccessToken(),
                })
                .configureLogging(settings.debugLevel)
                .withAutomaticReconnect({
                    nextRetryDelayInMilliseconds: (retryContext) => (settings.automaticReconnectTimeout),
                })
                .build();

            return conn;
        } catch (err) {
            console.log('init error', err);
            setError(error);
        }

        return false;
    }, [setError, settings]);

    const onUnauthorized = useCallback((receivedError) => {
        if (receivedError.statusCode === 401 && typeof settings.onUnauthorized === 'function') {
            settings.onUnauthorized();

            return true;
        }

        return false;
    }, [settings.onUnauthorized]);

    const start = useCallback(async (con) => (
        new Promise((resolve) => {
            if (con.state !== HubConnectionState.Connected) {
                try {
                    (async function startConnection() {
                        if (con.state !== HubConnectionState.Connecting) {
                            await con.start().catch((err) => {
                                const isUnauthorized = onUnauthorized(err);

                                if (isUnauthorized) {
                                    if (settings.debugLevel === LogLevel.Critical) {
                                        console.log('Unauthorized, reconnecting...');
                                    }

                                    stop();
                                    setConnectionState(HubConnectionState.Reconnecting);
                                    resolve(con);
                                }
                            });
                        }

                        await con.startPromise;
                        setConnectionState(con.state);
                        resolve(con);
                    }());
                } catch (err) {
                    const isUnauthorized = onUnauthorized(err);

                    if (isUnauthorized) {
                        stop();
                        setConnectionState(HubConnectionState.Reconnecting);
                        resolve(con);
                    }
                }
            } else {
                setConnectionState(con.state);
                resolve(con.state);
            }
        })
    ), [setError, setConnectionState, onUnauthorized]);

    const stop = useCallback(() => {
        if (connection) {
            connection.off();
        }
    }, [connection]);

    const initAndStart = useCallback(async () => {
        try {
            stop();
            const conn = init();

            setConnection(await start(conn));
            setError(undefined);
        } catch (err) {
            setError(err);
        }
    }, [setError, settings]);

    const setup = (configs) => {
        setSettings({ ...settings, ...configs });
    };

    useEffect(() => {
        if (settings.url && settings.accessToken) { initAndStart(); }
    }, [settings]);

    return useMemo(() => ({
        connection,
        connectionState,
        setup,
        stop,
        error,
        onUnauthorized,
    }), [
        connection,
        connectionState,
        setup,
        stop,
        error,
        onUnauthorized,
    ]);
};
